/** CODENAME: BLOCKBUSTER */
import { API } from '@neurotrack/web-core'

const SET_ACCESS = 'SET_ACCESS'
const SET_ERROR = 'SET_ERROR'
const SET_NORECORD_ERROR = 'SET_NORECORD_ERROR'
const SET_VIDEO_TRACK = 'SET_VIDEO_TRACK'
const SET_STREAM = 'SET_STREAM'
const SET_CAMERA_USER_DECLINED = 'SET_CAMERA_USER_DECLINED'

const constraints = {
  audio: false,
  video: {
    facingMode: 'user',
    width: { min: 640, ideal: 1280, max: 1920 },
    height: { min: 480, ideal: 720, max: 1080 }
  }
}

export default {
  namespaced: true,
  state: () => ({
    stream: undefined,
    videoTrack: undefined,
    accessToCamera: false,
    userDeclinedCamera: false,
    cameraOn: false,
    consentedForVideo: false,
    error: false,
    errUserDenied: false, // user did not accept cam
    errUserDismissed: false, // user closed browser cam auth modal
    errBelowSpec: false, // incapable of 480p at 30fps
    errNoRecorder: false, // can't record a stream
    clipsMap: {}
  }),
  getters: {
    getCameraHasError: state => (!state.accessToCamera || state.error)
  },
  mutations: {
    [SET_ACCESS](state, haveCameraAccess) {
      state.accessToCamera = haveCameraAccess
    },
    [SET_NORECORD_ERROR](state, error) {
      state.errNoRecorder = error
    },
    [SET_ERROR](state, error) {
      /** Reset error flags */
      state.errUserDenied = false
      state.errUserDismissed = false
      state.errBelowSpec = false

      const { name, message } = error ?? {}
      if (name === 'NotAllowedError') {
        if (message === 'Permission denied') {
          state.errUserDenied = true
        } else {
          state.errUserDismissed = true
        }
      } else if (name === 'OverconstrainedError') {
        state.errBelowSpec = true
      }
      state.error = !!error && state.errNoRecorder
    },
    [SET_VIDEO_TRACK](state, videoTrack) {
      state.videoTrack = videoTrack
    },
    [SET_STREAM](state, stream) {
      state.stream = stream
      state.cameraOn = !!stream
    },
    [SET_CAMERA_USER_DECLINED](state, decline = true) {
      state.userDeclinedCamera = decline
    }
  },
  actions: {
    async checkAccess({ commit }) {
      await navigator.mediaDevices.getUserMedia(constraints)
      const devices = await navigator.mediaDevices.enumerateDevices()
      const accessibleVideoDevices = devices.filter(
        ({ kind }) => kind === 'videoinput'
      )
      // ONLY if user has auth'd cam will devices have id's
      const hasAccess =
        accessibleVideoDevices?.filter(({ label }) => !!label)?.length > 0

      commit(SET_ACCESS, hasAccess)
    },
    async checkMediaRecorder({ commit }) {
      let errNoRecorder = false
      try {
        if (
          !MediaRecorder.isTypeSupported('video/webm; codecs=vp9')
          && !MediaRecorder.isTypeSupported('video/webm; codecs=vp8')
          && !MediaRecorder.isTypeSupported('video/mp4; codecs="avc1.42E01E"')
        ) {
          errNoRecorder = true
        }
      } catch (e) {
        errNoRecorder = true
      }
      commit(SET_NORECORD_ERROR, errNoRecorder)
    },
    async getCamera({ commit, dispatch }) {
      await dispatch('checkMediaRecorder')

      let error = false

      const stream = await navigator.mediaDevices
        .getUserMedia(constraints)
        .catch(ex => {
          error = true
          commit(SET_ERROR, ex)
        })

      if (!error) {
        commit(SET_STREAM, stream)
        commit(SET_ACCESS, true)
        const videoTrack = stream.getVideoTracks()?.[0]
        commit(SET_VIDEO_TRACK, videoTrack)
        commit(SET_ERROR, false)
      }
    },
    stopCamera({ commit, state }) {
      // stop video track if we have one
      state.videoTrack?.stop()
      // ensure this stream and videotrack are removed
      commit(SET_STREAM, undefined)
      commit(SET_VIDEO_TRACK, undefined)
    },
    async uploadEoP(context, {
      clip, assessmentId, userId, contentType
    }) {
      const res = await API.exam
        .eopUpload(assessmentId, userId, clip, contentType)
        .catch(ex => ex)
      return res
    },

    declineCamera({ commit }) {
      commit(SET_CAMERA_USER_DECLINED, true)
    }
  }
}
